import type { ParseKeys } from 'i18next';
import type { StudyStatus } from '@/api/generated/types.gen';
import type { DashboardGeometryStatus } from '@/hooks/useDashboardGeometries';
import type { StatusKey } from '@/lib/statusTone';

/**
 * The status → i18n-key vocabulary, namespaced per status enum.
 *
 * The sibling of `statusTone.ts`, and it exists for the same reason at the
 * same altitude. That file replaced ~60 independent status→colour tables with
 * one namespaced set of maps; the WORDS had no such home, so each product's
 * status badge owned its own map inside its own route folder. That was fine
 * while each map had exactly one reader — and stopped being fine the moment a
 * surface outside those routes (the home page's recents grid, ENG-2028) needed
 * to render a mesh and a study side by side.
 *
 * The alternative was for home to import from `meshgen/-components/` and
 * `simops/-components/`. Those folders are documented as private to their route
 * group and are refactored on that understanding, so a rename there would have
 * broken a build nobody making it was looking at.
 *
 * **Keys, not resolved strings**, exactly as before: that is what lets these
 * live at module scope while still switching with the selected language —
 * callers resolve with `t()` at render.
 *
 * Namespaced rather than flat for `statusTone.ts`'s reason, which applies here
 * too: `completed` and `failed` are members of more than one vocabulary, and a
 * flat table would force one product's wording onto another's state.
 */

/** `StudyStatus` — a whole SimOps study. */
export const STUDY_STATUS_LABEL_KEYS = {
  draft: 'simopsMisc.statusBadge.draft',
  preprocessing: 'simopsMisc.statusBadge.preprocessing',
  awaiting_user_meshing: 'simopsMisc.statusBadge.awaitingUserMeshing',
  awaiting_bc_review: 'simopsMisc.statusBadge.awaitingBcReview',
  awaiting_mobo_review: 'simopsMisc.statusBadge.awaitingMoboReview',
  running: 'simopsMisc.statusBadge.running',
  completed: 'simopsMisc.statusBadge.completed',
  completed_with_issues: 'simopsMisc.statusBadge.completedWithIssues',
  failed: 'simopsMisc.statusBadge.failed',
  cancelled: 'simopsMisc.statusBadge.cancelled',
} as const satisfies Record<StudyStatus, ParseKeys>;

/**
 * `DashboardGeometryStatus` — the MeshGen library card's frontend-only state.
 *
 * "Upload Error" was misleading — the backend's `status='error'` covers both
 * upload failures and failed diagnosis. Most rows that land there are diagnosis
 * failures (the upload itself usually succeeds), so the copy is the compact
 * "Diag. Error" (ENG-3124 Status chip width). If the backend ever distinguishes
 * the two, split this into two dashboard statuses.
 */
export const GEOMETRY_STATUS_LABEL_KEYS = {
  processing: 'meshgen.status.processing',
  error: 'meshgen.status.error',
  no_mesh: 'meshgen.status.noMesh',
  meshing: 'meshgen.status.meshing',
  done: 'meshgen.status.done',
  failed: 'meshgen.status.failed',
} as const satisfies Record<DashboardGeometryStatus, ParseKeys>;

/**
 * Stripe's `subscription.status` — the five values the billing panel names
 * (ENG-1146).
 *
 * Keyed off `SUBSCRIPTION` in `statusTone.ts`, which owns the choice of subset
 * and justifies the three Stripe statuses neither map carries. `satisfies`
 * against `StatusKey<'subscription'>` is what keeps the words and the colours
 * from drifting apart.
 */
export const SUBSCRIPTION_STATUS_LABEL_KEYS = {
  active: 'account.usage.status.active',
  trialing: 'account.usage.status.trialing',
  past_due: 'account.usage.status.pastDue',
  unpaid: 'account.usage.status.unpaid',
  canceled: 'account.usage.status.canceled',
  paused: 'account.usage.status.paused',
} as const satisfies Record<StatusKey<'subscription'>, ParseKeys>;

/**
 * Narrow `GET /subscription`'s open `status` string to one this panel names.
 *
 * `null` for anything else, including `null` itself (no subscription) and a
 * status Stripe adds later — `paused` was added in 2022, so the set does grow.
 * A chip is a claim about state; silence is the honest answer to a word we do
 * not know.
 *
 * `Object.hasOwn`, not `key in map`: the map is an object literal, so `in`
 * walks the prototype chain and a status spelled `constructor`, `toString` or
 * `valueOf` would pass the narrow and resolve to a *function* — which the
 * caller then hands to `t()` as a translation key. Unrecognised is exactly the
 * case this function exists for, so it must not be the case it gets wrong.
 * Same guard, for the same reason, as both lookups in `statusTone()`.
 */
export function subscriptionStatusKey(
  status: string | null | undefined,
): StatusKey<'subscription'> | null {
  return status != null && Object.hasOwn(SUBSCRIPTION_STATUS_LABEL_KEYS, status)
    ? (status as StatusKey<'subscription'>)
    : null;
}
